/**
 * Enhances the price facet UI with a dual-range slider that drives the existing
 * hidden min/max inputs used by `price-facet-component` (facets.js).
 */

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function parseNumber(value, fallback = 0) {
  const parsed = Number(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : fallback;
}

function setCSSPercentVars(rangeEl, minValue, maxValue, rangeMax) {
  const safeMax = Math.max(rangeMax, 1);
  const minPct = clamp((minValue / safeMax) * 100, 0, 100);
  const maxPct = clamp((maxValue / safeMax) * 100, 0, 100);
  rangeEl.style.setProperty("--min-pct", `${minPct}%`);
  rangeEl.style.setProperty("--max-pct", `${maxPct}%`);
}

function formatMoneyLike(value, symbol) {
  const n = Math.round(parseNumber(value, 0));
  return `${symbol}${n.toLocaleString()}`;
}

function enhancePriceFacet(priceFacetEl) {
  const rangeEl = priceFacetEl.querySelector(".custom-facets-range .price-facet__range");
  if (!rangeEl) return;

  const minRange = rangeEl.querySelector('[data-price-range="min"]');
  const maxRange = rangeEl.querySelector('[data-price-range="max"]');
  if (!(minRange instanceof HTMLInputElement) || !(maxRange instanceof HTMLInputElement)) return;

  // These are the real inputs used by facets.js.
  const minInput = priceFacetEl.querySelector('[ref="minInput"]');
  const maxInput = priceFacetEl.querySelector('[ref="maxInput"]');
  if (!(minInput instanceof HTMLInputElement) || !(maxInput instanceof HTMLInputElement)) return;

  const minValueEl = priceFacetEl.querySelector('[data-price-value="min"]');
  const maxValueEl = priceFacetEl.querySelector('[data-price-value="max"]');
  const currencySymbol = rangeEl.getAttribute("data-currency-symbol") || "$";
  const rangeMax = parseNumber(rangeEl.getAttribute("data-range-max") || maxRange.max, 0);

  function syncFromRanges() {
    let minValue = parseNumber(minRange.value, 0);
    let maxValue = parseNumber(maxRange.value, rangeMax);

    // Prevent crossing handles.
    if (minValue > maxValue) minValue = maxValue;
    if (maxValue < minValue) maxValue = minValue;

    // Keep UI inputs in sync in case we clamped.
    minRange.value = String(minValue);
    maxRange.value = String(maxValue);

    // Write hidden inputs. Blank means "not constrained".
    minInput.value = minValue <= 0 ? "" : String(minValue);
    maxInput.value = rangeMax > 0 && maxValue >= rangeMax ? "" : String(maxValue);

    setCSSPercentVars(rangeEl, minValue, maxValue, rangeMax);

    if (minValueEl instanceof HTMLElement) minValueEl.textContent = formatMoneyLike(minValue, currencySymbol);
    if (maxValueEl instanceof HTMLElement) maxValueEl.textContent = formatMoneyLike(maxValue, currencySymbol);
  }

  // Live updates while dragging.
  minRange.addEventListener("input", syncFromRanges);
  maxRange.addEventListener("input", syncFromRanges);

  // Initial state.
  syncFromRanges();
}

// After your existing init() function, observe DOM mutations for re-rendered facets:
function observeFacetUpdates() {
  const observer = new MutationObserver(() => {
    document.querySelectorAll(".custom-facets-range price-facet-component.price-facet:not([data-enhanced])").forEach((el) => {
      if (el instanceof HTMLElement) {
        el.setAttribute("data-enhanced", "true");
        enhancePriceFacet(el);
      }
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

function init() {
  document.querySelectorAll(".custom-facets-range price-facet-component.price-facet").forEach((el) => {
    if (el instanceof HTMLElement) {
      el.setAttribute("data-enhanced", "true");
      enhancePriceFacet(el);
    }
  });
  observeFacetUpdates();
}


if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}
